

import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { InputBlob } from "@/components/Inputs/InputBlob";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Spacing } from "@/components/Spacing/Spacing";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { useTranslations } from "next-intl";
import {
  IconCopy,
  IconCheck,
  IconAlertCircle,
  IconDownload,
} from "@tabler/icons-react";
import { useMfaSetupForm } from "./useMfaSetupForm";

export default function MfaSetupContent({
  initialTimeRemaining,
}: {
  initialTimeRemaining: number;
}) {
  const t = useTranslations("mfaSetup");
  const [copied, setCopied] = useState(false);

  const {
    step,
    secret,
    otpAuthUri,
    otp,
    recoveryCodes,
    codesSaved,
    error,
    isLoading,
    activeInput,
    timeRemaining,
    sessionWarning,
    copyCodesState,
    downloadCodesState,
    handleStart,
    handleScanned,
    handleVerify,
    handleDownloadCodes,
    handleCopyCodes,
    handleComplete,
    handleCancel,
    handleChange,
    handleKeyDown,
    handlePaste,
    updateState,
  } = useMfaSetupForm(initialTimeRemaining);

  function handleCopySecret() {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex flex-col items-center w-full">
      {/* Session warning banner — steps 1-3 only */}
      {sessionWarning && timeRemaining > 0 && step !== 4 && (
        <div className="w-full p-2 mb-2 rounded-lg surface-warning text-center">
          <p className="body-2-regular font-warning">
            {t("sessionWarning", { seconds: timeRemaining })}
          </p>
        </div>
      )}

      {/* Step 1 — Info */}
      {step === 1 && (
        <>
          <h3 className="h3 text-center">{t("step1Title")}</h3>
          <Spacing height="h-4" />
          <p className="body-2-regular font-secondary text-center">
            {t("step1Description")}
          </p>

          {error && (
            <p role="alert" data-testid="mfa-setup-error" className="font-error body-2-regular mt-2">
              {error}
            </p>
          )}

          <Spacing height="h-6" />
          <PrimaryButton
            size="small"
            text={t("getStarted")}
            onClick={handleStart}
            disabled={isLoading || timeRemaining === 0}
            loading={isLoading}
          />
          <Spacing height="h-3" />
          <SecondaryButton size="small" text={t("cancel")} onClick={handleCancel} />
        </>
      )}

      {/* Step 2 — QR Code */}
      {step === 2 && (
        <>
          <Spacing height="h-4" />
          <h3 className="h3 text-center">{t("step2Title")}</h3>
          <Spacing height="h-4" />
          <p className="body-2-regular font-secondary text-center">
            {t("scanQrCode")}
          </p>
          <Spacing height="h-8" />

          {/* bg-white is intentional: QR codes require a white background for scanner readability */}
          <div className="p-4 bg-white rounded-xl">
            <QRCodeSVG value={otpAuthUri} size={200} />
          </div>

          <Spacing height="h-6" />
          <p className="body-2-regular font-secondary text-center">
            {t("manualEntry")}
          </p>
          <Spacing height="h-2" />

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <code
              data-testid="mfa-setup-manual-secret"
              className="font-mono body-2-regular font-primary select-all break-all"
            >
              {secret}
            </code>
            <button
              type="button"
              onClick={handleCopySecret}
              className="body-2-regular font-blue whitespace-nowrap cursor-pointer hover:opacity-70 transition-opacity duration-200"
            >
              {copied ? t("copied") : t("copySecret")}
            </button>
          </div>

          <Spacing height="h-6" />
          <PrimaryButton
            size="small"
            text={t("scannedContinue")}
            onClick={handleScanned}
          />
          <Spacing height="h-3" />
          <SecondaryButton size="small" text={t("cancel")} onClick={handleCancel} />
        </>
      )}

      {/* Step 3 — Verify */}
      {step === 3 && (
        <form onSubmit={handleVerify} className="flex flex-col items-center w-full">
          <Spacing height="h-4" />
          <h3 className="h3 text-center">{t("step3Title")}</h3>
          <Spacing height="h-4" />
          <p className="body-2-regular font-secondary text-center">
            {t("step3Description")}
          </p>
          <Spacing height="h-8" />

          <div className="flex space-x-2">
            {otp.map((digit, index) => (
              <InputBlob
                key={index}
                index={index}
                value={digit}
                isActive={index === activeInput}
                changAction={handleChange}
                keyDownAction={handleKeyDown}
                onPaste={index === 0 ? handlePaste : undefined}
              />
            ))}
          </div>

          {error && (
            <p role="alert" data-testid="mfa-setup-error" className="font-error body-2-regular mt-2">
              {error}
            </p>
          )}

          <Spacing height="h-6" />
          <PrimaryButton
            type="submit"
            size="small"
            text={t("verify")}
            disabled={isLoading || timeRemaining === 0}
            loading={isLoading}
          />
          <Spacing height="h-3" />
          <SecondaryButton size="small" text={t("cancel")} onClick={handleCancel} />
        </form>
      )}

      {/* Step 4 — Recovery Codes */}
      {step === 4 && (
        <>
          <Spacing height="h-4" />
          <h3 className="h3 text-center">{t("step4Title")}</h3>
          <Spacing height="h-4" />
          <p className="body-2-regular font-secondary text-center">
            {t("step4Description")}
          </p>
          <Spacing height="h-6" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
            {recoveryCodes.map((code, index) => (
              <code
                key={index}
                data-testid="mfa-setup-recovery-code"
                className="font-mono body-2-regular font-primary text-center py-1 px-2 surface-secondary rounded-lg"
              >
                {code}
              </code>
            ))}
          </div>

          <Spacing height="h-6" />

          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <SecondaryButton
              size="small"
              text={t("downloadCodes")}
              onClick={handleDownloadCodes}
              icon={downloadCodesState === "done" ? IconCheck : IconDownload}
              testId="mfa-setup-download-codes"
            />
            <SecondaryButton
              size="small"
              text={copyCodesState === "copied" ? t("copied") : t("copyAll")}
              onClick={handleCopyCodes}
              icon={
                copyCodesState === "copied"
                  ? IconCheck
                  : copyCodesState === "error"
                    ? IconAlertCircle
                    : IconCopy
              }
              testId="mfa-setup-copy-codes"
            />
          </div>

          <Spacing height="h-6" />

          <div className="self-start">
            <CheckboxWithValidation
              isChecked={codesSaved}
              onCheckChangeAction={(checked) => updateState({ codesSaved: checked })}
              testId="mfa-setup-recovery-saved"
            >
              {t("savedCheckbox")}
            </CheckboxWithValidation>
          </div>

          <Spacing height="h-6" />

          <PrimaryButton
            size="small"
            text={t("completeSetup")}
            onClick={handleComplete}
            // `handleComplete` awaits the account hydration, so without a busy
            // state the button stays live for up to 1.5s and a second click
            // fires a second markAuthenticated + navigate.
            disabled={!codesSaved || isLoading}
            loading={isLoading}
          />
        </>
      )}
    </div>
  );
}
